import {
  makeObservable,
  observable,
  action,
  runInAction,
  computed,
  autorun,
} from "mobx";
import { NotificationManager } from "react-notifications";
import queryString from "query-string";
import {
  getPopularArticles,
  addArticleToUserPlaylist,
  getUserPlaylist,
  getSearchResults,
  updateArticleInPlaylist,
  getUserFeeds,
  deleteUserFeed,
  getLatestFeedArticleFromFeed,
  getAllFeeds,
} from "../../audiblogsApi";
import { publish, subscribe } from "../../PlayerPubSub";
import userStore from "../../Stores/User";
import history from "../../history";
import mixpanel from "mixpanel-browser";
import { debounce } from "lodash";

const getRandomInt = (min, max) => {
  min = Math.ceil(min);
  max = Math.floor(max);

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const getCurrentUrl = () =>
  window.location.pathname +
  (window.location.search.length > 0 ? `?${window.location.search}` : "");

const defaultUrl = getCurrentUrl();

const defaultSearch =
  defaultUrl.indexOf("/search") !== -1
    ? (queryString.parse(window.location.search).q || "").trim()
    : "";

const defaultAddUrl =
  defaultUrl.indexOf("/add") !== -1
    ? (
        queryString.parse(window.location.search).url ||
        queryString.parse(window.location.search).url_description ||
        queryString.parse(window.location.search).url_title ||
        ""
      ).trim()
    : "";
const defaultSelectionText =
  defaultUrl.indexOf("/add") !== -1
    ? (queryString.parse(window.location.search).selectionText || "").trim()
    : "";

const defaultAddApp =
  defaultUrl.indexOf("/add") !== -1
    ? (queryString.parse(window.location.search).app || "").trim()
    : "";

const defaultAddForce =
  defaultUrl.indexOf("/add") !== -1
    ? (queryString.parse(window.location.search).force || "").trim()
    : "";

const defaultMode =
  defaultUrl.indexOf("/playlist") !== -1
    ? "playlist"
    : defaultUrl.indexOf("/add") !== -1
    ? "addArticle"
    : defaultUrl.indexOf("/feeds") !== -1
    ? "feeds"
    : "discover";

const defaultMobileSettingsActive = defaultUrl.indexOf("/settings") !== -1;

const updateUrlFromMode = ({
  mobileMode,
  mode,
  mobileSettingsActive,
  search,
  addUrl,
  addApp,
  addForce,
  selectionText,
}) => {
  let url = "";
  switch (mobileMode) {
    case "settings":
      url = "/settings";
      break;
    case "playlist":
      url = "/playlist";
      break;
    case "feeds":
      url = "/feeds";
      break;
    case "addArticle":
      url =
        addUrl.trim().length > 0
          ? `/add?url=${encodeURIComponent(addUrl)}&app=${encodeURIComponent(
              addApp
            )}${
              selectionText &&
              "&selectionText=" + encodeURIComponent(selectionText)
            }&force=${encodeURIComponent(addForce)}`
          : "/add";
      break;
    default:
      url =
        search.trim().length > 0
          ? `/search?q=${encodeURIComponent(search)}`
          : "/";
      break;
  }

  if (getCurrentUrl() !== url) {
    history.push(url, {
      mode,
      mobileSettingsActive,
      search,
      addUrl,
      addApp,
      addForce,
      selectionText,
    });
  }
};

class DiscoverStore {
  isPlaying = false;
  isFullPlayer = false;
  loading = false;
  mode = defaultMode;
  playlistMode = "all";
  playlistSearchQuery = "";
  mobileSettingsActive = defaultMobileSettingsActive;
  addUrl = defaultAddUrl;
  addApp = defaultAddApp;
  addForce = defaultAddForce;
  selectionText = defaultSelectionText;
  search = defaultSearch;
  pendingSearch = defaultSearch;
  daysAgo = 7;
  currentArticle = null;
  popularArticles = [];
  searchArticles = [];
  similarArticles = [];
  playlistArticles = null;
  userFeeds = [];
  allFeeds = [];
  feedsLoading = false;
  allFeedsLoading = false;
  daysAgoOptions = [1, 2, 3, 5, 7, 14, 30, 60, 90];
  playlistUpdatesPaused = false;
  mounted = false;
  lastUpdatedSearch = null;
  feedCache = new Map();
  feedCacheTimestamp = null;
  FEED_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {
    makeObservable(this, {
      isPlaying: observable,
      isFullPlayer: observable,
      loading: observable,
      mode: observable,
      playlistMode: observable,
      playlistSearchQuery: observable,
      mobileSettingsActive: observable,
      addUrl: observable,
      addApp: observable,
      addForce: observable,
      selectionText: observable,
      search: observable,
      pendingSearch: observable,
      daysAgo: observable,
      currentArticle: observable,
      popularArticles: observable.shallow,
      searchArticles: observable.shallow,
      similarArticles: observable.shallow,
      playlistArticles: observable.shallow,
      userFeeds: observable.shallow,
      allFeeds: observable.shallow,
      feedsLoading: observable,
      allFeedsLoading: observable,
      daysAgoOptions: observable.shallow,
      playlistUpdatesPaused: observable,
      mounted: observable,
      lastUpdatedSearch: observable,
      playlistArticlesToShow: computed,
      mobileMode: computed,
      updateSelectionText: action.bound,
      updateAddUrl: action.bound,
      updateAddApp: action.bound,
      updateAddForce: action.bound,
      changeMode: action.bound,
      changePlaylistMode: action.bound,
      updatePlaylistSearch: action.bound,
      fetchPopularArticles: action.bound,
      fetchUserFeeds: action.bound,
      fetchAllFeeds: action.bound,
      unsubscribeFromFeed: action.bound,
      addArticleToPlaylist: action.bound,
      addTemporaryArticle: action.bound,
      replaceTemporaryArticle: action.bound,
      removeTemporaryArticle: action.bound,
      loadPlaylist: action.bound,
      // loadSimilarArticles: action.bound,
      changeCurrentArticle: action.bound,
      changeCurrentArticleForcePlay: action.bound,
      updateSearch: action.bound,
      updatePendingSearch: action.bound,
      updateDaysAgo: action.bound,
      onNextArticle: action.bound,
      onPreviousArticle: action.bound,
      onArticleEnded: action.bound,
      onPreviousNextArticle: action.bound,
      updateMobileSettingsActive: action.bound,
      updateIsFullPlayer: action.bound,
      togglePlayed: action.bound,
      removeArticle: action.bound,
      pausePlaylistUpdates: action.bound,
      unpausePlaylistUpdates: action.bound,
      updateStoreFromRouterState: action.bound,
      updateMounted: action.bound,
      subscribeToFeed: action.bound,
    });

    subscribe("playing", () => {
      runInAction(() => {
        this.isPlaying = true;
      });
    });

    subscribe("paused", () => {
      runInAction(() => {
        this.isPlaying = false;
      });
    });

    history.listen(({ state }) => {
      if (this.mounted) {
        this.updateStoreFromRouterState(state);
      }
    });

    autorun(() => {
      if (this.mounted) {
        updateUrlFromMode({
          mobileMode: this.mobileMode,
          mode: this.mode,
          mobileSettingsActive: this.mobileSettingsActive,
          search: this.search,
          addUrl: this.addUrl,
          addApp: this.addApp,
          addForce: this.addForce,
          selectionText: this.selectionText,
        });
      }
    });

    // TO DO: This errors out if mixpanel isn't initialized yet, when loading a page fresh with a search param
    this.updateSearch(this.search);
  }

  updateAddUrl(newAddUrl) {
    // Normalize URL if needed when updating
    if (newAddUrl && newAddUrl.trim() !== '' && !/^https?:\/\//i.test(newAddUrl)) {
      newAddUrl = `https://${newAddUrl.trim()}`;
    }
    this.addUrl = newAddUrl;
  }

  updateAddApp(newAddApp) {
    this.addApp = newAddApp;
  }

  updateAddForce(newAddForce) {
    this.addForce = newAddForce;
  }
  updateSelectionText(newSelectionText) {
    this.selectionText = newSelectionText;
  }

  changeMode(newMode) {
    if (this.mode === newMode) {
      return;
    }

    // Only clear search when switching to non-search modes
    if (newMode !== "discover" || this.search.trim().length === 0) {
      this.updateSearch("");
    }
    
    // Never clear current article when switching modes
    this.mode = newMode;

    // Load feeds when switching to feeds mode
    if (newMode === "feeds") {
      this.fetchUserFeeds();
      this.fetchAllFeeds();
    }

    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 1);
  }

  changePlaylistMode(newMode) {
    this.playlistMode = newMode; // "unplayed" or "played"
  }

  updatePlaylistSearch(query) {
    this.playlistSearchQuery = query;
  }

  fetchPopularArticles(assignRandom) {
    // Do nothing - we don't want to use popular articles
    this.loading = false;
  }

  changeCurrentArticleForcePlay(article) {
    return this.changeCurrentArticle(article, true);
  }

  changeCurrentArticle(article, forcePlay = false) {
    let shouldPlay = forcePlay || this.isPlaying;

    if (
      article &&
      this.currentArticle &&
      article.article_id === this.currentArticle.article_id
    ) {
      // If clicking on the same article, toggle play/pause
      shouldPlay = !this.isPlaying;
    } else {
      // If clicking on a different article, always play
      shouldPlay = true;
    }

    this.currentArticle = article;
    this.isPlaying = shouldPlay;

    // if (this.currentArticle) {
    //   this.loadSimilarArticles(article.article_id);
    // }

    setTimeout(() => publish(shouldPlay ? "play" : "pause"), 1);
  }

  addArticleToPlaylist(article) {
    NotificationManager.success("Added to playlist", "", 2000);

    addArticleToUserPlaylist(article, userStore.idToken, "/discover");
    // this.loadSimilarArticles(article.article_id);

    try{
      this.playlistArticles = [article, ...this.playlistArticles];
    } catch (e) {
      this.playlistArticles = [article];
    }
  }

  // Add a method to handle temporary articles (for real-time updates)
  addTemporaryArticle(tempArticle) {
    try {
      this.playlistArticles = [tempArticle, ...this.playlistArticles];
    } catch (e) {
      this.playlistArticles = [tempArticle];
    }
  }

  // Add a method to replace temporary article with real article
  replaceTemporaryArticle(tempArticleId, realArticle) {
    if (this.playlistArticles) {
      this.playlistArticles = this.playlistArticles.map(article => 
        article.article_id === tempArticleId ? realArticle : article
      );
    }
  }

  // Add a method to remove temporary article (in case of errors)
  removeTemporaryArticle(tempArticleId) {
    if (this.playlistArticles) {
      this.playlistArticles = this.playlistArticles.filter(article => 
        article.article_id !== tempArticleId
      );
    }
  }

  // loadSimilarArticles(url) {
  //   if (userStore.idToken) {
  //     getSimilarArticlesForUserV3(url, userStore.idToken).then(
  //       (similarArticles) =>
  //         runInAction(() => {
  //           this.similarArticles = similarArticles || [];
  //         })
  //     );
  //   }
  // }

  loadPlaylist = debounce(() => {
    if (this.playlistUpdatesPaused) {
      return;
    }

    getUserPlaylist(userStore.idToken).then((playlist) =>
      runInAction(() => {
        // Only update if the playlist has actually changed
        if (JSON.stringify(this.playlistArticles) !== JSON.stringify(playlist)) {
          this.playlistArticles = playlist || [];
          
          // Only set the current article if none is selected yet AND we're in playlist mode
          // Explicitly check we're not in feeds or addArticle mode
          if (this.playlistArticles.length > 0 && !this.currentArticle && 
              this.mode === "playlist" && this.mobileMode !== "feeds" && this.mobileMode !== "addArticle") {
            // Filter for unplayed articles and sort by date_added in descending order
            const unplayedArticles = this.playlistArticles.filter(article => 
              !article.deleted && !article.played
            );
            if (unplayedArticles.length > 0) {
              const mostRecentUnplayed = unplayedArticles.sort((a, b) => 
                new Date(b.date_added) - new Date(a.date_added)
              )[0];
              this.changeCurrentArticle(mostRecentUnplayed);
            }
          }
        }
      })
    );
  }, 1000);

  updatePendingSearch(newPendingSearch) {
    this.pendingSearch = newPendingSearch;
    this.lastUpdatedSearch = new Date().getTime();
    const priorLastUpdatedSearch = this.lastUpdatedSearch;

    setTimeout(() => {
      if (this.lastUpdatedSearch !== priorLastUpdatedSearch) {
        return;
      }

      this.updateSearch(this.pendingSearch);
    }, 250);
  }

  updateSearch(newSearch) {
    this.search = newSearch;
    this.pendingSearch = newSearch;

    if (this.search.trim().length > 0) {
      this.loading = true;

      mixpanel.track("Search Request");

      getSearchResults(this.search).then(({ articles }) =>
        runInAction(() => {
          this.searchArticles = articles;
          this.loading = false;
        })
      );
    } else {
      this.searchArticles = [];
    }
  }

  updateDaysAgo(newDaysAgo, assignRandom = false) {
    this.daysAgo = newDaysAgo;
    // Do not fetch popular articles anymore
  }

  onPreviousArticle() {
    this.onPreviousNextArticle(false);
  }

  onNextArticle() {
    this.onPreviousNextArticle(true);
  }

  onArticleEnded() {
    try {
      if (
        this.currentArticle &&
        !this.currentArticle.audio_url.includes("placeholder.mp3")
      ) {
        // NOTE: not sure if this works or if unpausePlaylistUpdates does anything
        updateArticleInPlaylist(
          this.currentArticle,
          userStore.idToken,
          "played",
          true
        ).then(this.unpausePlaylistUpdates, this.unpausePlaylistUpdates);
      }
    } catch (e) {
      console.log(e);
    }
  }

  onPreviousNextArticle(forward) {
    let findIndex = -1;
    let queryPlaylist = [];

    // First, try to find the current article in playlistArticles (most common case)
    if (this.currentArticle && this.playlistArticles && this.playlistArticles.length > 0) {
      findIndex = this.playlistArticles.findIndex(
        (item) => item.article_id === this.currentArticle.article_id
      );

      if (findIndex !== -1) {
        queryPlaylist = this.playlistArticles;
      }
    }

    // If not found in playlist, try searchArticles if we're in discover mode with search
    if (findIndex === -1 && this.currentArticle && this.mode === "discover" && this.search.trim().length > 0 && this.searchArticles && this.searchArticles.length > 0) {
      findIndex = this.searchArticles.findIndex(
        (item) => item.article_id === this.currentArticle.article_id
      );

      if (findIndex !== -1) {
        queryPlaylist = this.searchArticles;
      }
    }

    // If still not found but we have playlist articles, start from the beginning
    if (findIndex === -1 && this.playlistArticles && this.playlistArticles.length > 0) {
      findIndex = 0;
      queryPlaylist = this.playlistArticles;
    }

    // If we have a valid playlist and index, navigate
    if (queryPlaylist.length > 0 && findIndex !== -1) {
      let newIndex = findIndex + (forward ? 1 : -1);

      // Handle wrapping around the playlist
      if (newIndex < 0) {
        newIndex = queryPlaylist.length - 1;
      }

      if (newIndex >= queryPlaylist.length) {
        newIndex = 0;
      }

      this.changeCurrentArticle(queryPlaylist[newIndex]);
    } else {
      // No valid playlist found, clear current article
      this.changeCurrentArticle(null);
    }
  }

  updateMobileSettingsActive(settingsActive) {
    if (this.mobileSettingsActive === settingsActive) {
      return;
    }

    if (settingsActive) {
      this.updateSearch("");
    }

    this.mobileSettingsActive = settingsActive;
  }

  updateIsFullPlayer(isFullPlayer) {
    this.isFullPlayer = isFullPlayer;
  }

  togglePlayed(article) {
    this.pausePlaylistUpdates();

    this.playlistArticles = this.playlistArticles.map((currentArticle) =>
      currentArticle.article_id !== article.article_id
        ? currentArticle
        : { ...article, played: !article.played }
    );

    updateArticleInPlaylist(
      article,
      userStore.idToken,
      "played",
      !article.played
    ).then(this.unpausePlaylistUpdates, this.unpausePlaylistUpdates);
  }

  removeArticle(articleToRemove) {
    this.pausePlaylistUpdates();

    this.playlistArticles = this.playlistArticles.filter(
      (article) => article.article_id !== articleToRemove.article_id
    );

    updateArticleInPlaylist(
      articleToRemove,
      userStore.idToken,
      "deleted",
      true
    ).then(this.unpausePlaylistUpdates, this.unpausePlaylistUpdates);
  }

  pausePlaylistUpdates() {
    this.playlistUpdatesPaused = true;
  }

  unpausePlaylistUpdates() {
    this.playlistUpdatesPaused = false;
  }

  updateStoreFromRouterState(state) {
    if (
      state &&
      state.mode !== undefined &&
      state.mobileSettingsActive !== undefined &&
      state.search !== undefined &&
      state.addUrl !== undefined &&
      state.addApp !== undefined &&
      state.addForce !== undefined &&
      state.selectionText !== undefined
    ) {
      if (this.mode !== state.mode) {
        this.changeMode(state.mode);
      }

      if (this.mobileSettingsActive !== state.mobileSettingsActive) {
        this.updateMobileSettingsActive(state.mobileSettingsActive);
      }

      if (this.search !== state.search) {
        this.updateSearch(state.search);
      }

      if (this.addUrl !== state.addUrl) {
        this.updateAddUrl(state.addUrl);
      }

      if (this.addApp !== state.addApp) {
        this.updateAddApp(state.addApp);
      }

      if (this.addForce !== state.addForce) {
        this.updateAddForce(state.addForce);
      }

      if (this.selectionText !== state.selectionText) {
        this.updateSelectionText(state.selectionText);
      }
    }
  }

  updateMounted(mounted) {
    this.mounted = mounted;
  }

  get playlistArticlesToShow() {
    if (this.playlistArticles !== null) {
      return this.playlistArticles.filter(
        (article) =>
          !article.deleted &&
          (this.playlistMode === "all"
            ? true
            : this.playlistMode === "unplayed"
            ? !article.played
            : article.played)
      );
    } else {
      return null;
    }
  }

  get mobileMode() {
    if (this.mobileSettingsActive) {
      return "settings";
    }

    switch (this.mode) {
      case "playlist":
        return "playlist";
      case "addArticle":
        return "addArticle";
      case "feeds":
        return "feeds";
      default:
        return "discover";
    }
  }

  isFeedCacheValid = () => {
    return this.feedCacheTimestamp && 
           (Date.now() - this.feedCacheTimestamp < this.FEED_CACHE_DURATION);
  }

  clearFeedCache = () => {
    this.feedCache.clear();
    this.feedCacheTimestamp = null;
  }

  fetchUserFeeds = debounce(async () => {
    if (!userStore.idToken) return;
    if (this.feedsLoading) return;
    this.feedsLoading = true;
    
    try {
      const response = await getUserFeeds(userStore.idToken);
      if (response && response.data && response.data.feeds) {
        const processedFeeds = response.data.feeds.map(feed => {
          const cachedFeed = this.feedCache.get(feed.feed_id);
          return {
            ...feed,
            feed_id: feed.feed_id || feed.id || `feed-${Math.random()}`,
            title: feed.title || feed.name || "Untitled Feed",
            date_added: feed.date_added || feed.created_at || new Date().toISOString(),
            article_id: feed.last_article_url || feed.feed_id || feed.id,
            audio_url: feed.audio_url || "https://audioread.com/share/placeholder.mp3",
            // Use cached data if available
            thumbnail_url: cachedFeed?.thumbnail_url || feed.thumbnail_url,
            latestArticle: cachedFeed?.latestArticle
          };
        });
        
        runInAction(() => {
          this.userFeeds = processedFeeds;
          this.feedsLoading = false;
        });
      } else {
        runInAction(() => {
          this.userFeeds = [];
          this.feedsLoading = false;
        });
      }
    } catch (error) {
      console.error("Error fetching user feeds:", error);
      runInAction(() => {
        this.userFeeds = [];
        this.feedsLoading = false;
      });
    }
  }, 1000);

  fetchAllFeeds = debounce(async () => {
    if (this.allFeedsLoading) return;
    this.allFeedsLoading = true;
    
    try {
      // Use cached data if valid
      if (this.isFeedCacheValid()) {
        runInAction(() => {
          this.allFeeds = Array.from(this.feedCache.values());
          this.allFeedsLoading = false;
        });
        return;
      }

      const response = await getAllFeeds();
      if (response && response.data && response.data.feeds) {
        const processedFeeds = response.data.feeds.map(feed => ({
          ...feed,
          feed_id: feed.feed_id || feed.id || `feed-${Math.random()}`,
          title: feed.title || feed.name || "Untitled Feed",
          date_added: feed.date_added || feed.created_at || new Date().toISOString(),
          article_id: feed.last_article_url || feed.feed_id || feed.id,
          audio_url: feed.audio_url || "https://audioread.com/share/placeholder.mp3",
        }));
        
        // Update cache
        processedFeeds.forEach(feed => {
          this.feedCache.set(feed.feed_id, feed);
        });
        this.feedCacheTimestamp = Date.now();
        
        runInAction(() => {
          this.allFeeds = processedFeeds;
          this.allFeedsLoading = false;
        });
      } else {
        runInAction(() => {
          this.allFeeds = [];
          this.allFeedsLoading = false;
        });
      }
    } catch (error) {
      console.error("Error fetching all feeds:", error);
      runInAction(() => {
        this.allFeeds = [];
        this.allFeedsLoading = false;
      });
    }
  }, 1000);

  unsubscribeFromFeed(feedId) {
    // Remove the feed from both local states immediately for better UX
    runInAction(() => {
      this.userFeeds = this.userFeeds.filter(feed => feed.feed_id !== feedId);
      // Also remove from allFeeds if it exists there
      this.allFeeds = this.allFeeds.filter(feed => feed.feed_id !== feedId);
    });
  }

  subscribeToFeed(feed) {
    // Add the feed to both local states immediately for better UX
    runInAction(() => {
      // Add to userFeeds
      this.userFeeds = [feed, ...this.userFeeds];
      // Remove from allFeeds if it exists there
      this.allFeeds = this.allFeeds.filter(f => f.feed_id !== feed.feed_id);
    });
  }
}

const discoverStore = new DiscoverStore();

export default discoverStore;
